import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { Save, ChevronDown, Send, Eye, X } from "lucide-react";
import { ASSESSMENT_TYPES, SECTIONS } from "@/lib/oasisSections";

function FieldInput({ item, value, onChange }) {
  const baseCls = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 outline-none";
  const monoCls = item.mono ? "font-mono uppercase" : "";

  if (item.type === "textarea") {
    return <textarea data-testid={`oasis-${item.id}-coder`} rows={2} value={value || ""} onChange={(e) => onChange(e.target.value)} className={baseCls} />;
  }
  if (item.type === "select") {
    return (
      <select data-testid={`oasis-${item.id}-coder`} value={value || ""} onChange={(e) => onChange(e.target.value)} className={baseCls}>
        <option value="">—</option>
        {item.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (item.type === "radio") {
    return (
      <div className="flex flex-wrap gap-2">
        {item.options.map((o) => (
          <label key={o} className={`text-xs px-3 py-1.5 rounded-lg border cursor-pointer ${value === o ? "border-sky-500 bg-sky-50 text-sky-800" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            <input type="radio" name={`${item.id}-coder`} value={o} checked={value === o} onChange={() => onChange(o)} className="hidden" data-testid={`oasis-${item.id}-coder-${o.replace(/\W+/g, "_")}`} />
            {o}
          </label>
        ))}
      </div>
    );
  }
  if (item.type === "checklist") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div className="flex flex-wrap gap-2">
        {item.options.map((o) => {
          const checked = arr.includes(o);
          return (
            <label key={o} className={`text-xs px-3 py-1.5 rounded-lg border cursor-pointer ${checked ? "border-sky-500 bg-sky-50 text-sky-800" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
              <input type="checkbox" checked={checked} onChange={() => onChange(checked ? arr.filter((x) => x !== o) : [...arr, o])} className="hidden" data-testid={`oasis-${item.id}-coder-${o.replace(/\W+/g, "_")}`} />
              {o}
            </label>
          );
        })}
      </div>
    );
  }
  return <input data-testid={`oasis-${item.id}-coder`} type={item.type} value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${baseCls} ${monoCls}`} />;
}

function DiagnosisRow({ item, value, onChange }) {
  // value shape: { date, code, oe, severity }
  const v = value || {};
  const isZ = (v.code || "").trim().toUpperCase().startsWith("Z");
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/30 sm:col-span-2" data-testid={`oasis-item-${item.id}`}>
      <label className="block text-sm font-medium text-slate-800 mb-2">{item.label}</label>
      <input type="text" value={v.code || ""}
             onChange={(e) => onChange({ ...v, code: e.target.value.toUpperCase() })}
             data-testid={`oasis-${item.id}-code`}
             placeholder="ICD-10 code"
             className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono bg-white focus:ring-2 focus:ring-sky-500 outline-none mb-2"/>
      <div className={`grid gap-2 ${isZ ? "sm:grid-cols-2" : "sm:grid-cols-3"}`}>
        <input type="date" value={v.date || ""}
               onChange={(e) => onChange({ ...v, date: e.target.value })}
               data-testid={`oasis-${item.id}-date`}
               className="border border-slate-300 rounded-lg px-2.5 py-2 text-sm bg-white focus:ring-2 focus:ring-sky-500 outline-none"/>
        <select value={v.oe || ""}
                onChange={(e) => onChange({ ...v, oe: e.target.value })}
                data-testid={`oasis-${item.id}-oe`}
                className="border border-slate-300 rounded-lg px-2 py-2 text-sm bg-white focus:ring-2 focus:ring-sky-500 outline-none">
          <option value="">O / E</option>
          <option value="O">O - Onset</option>
          <option value="E">E - Exacerbation</option>
        </select>
        {!isZ && (
          <select value={v.severity || ""}
                  onChange={(e) => onChange({ ...v, severity: e.target.value })}
                  data-testid={`oasis-${item.id}-severity`}
                  className="border border-slate-300 rounded-lg px-2 py-2 text-sm bg-white focus:ring-2 focus:ring-sky-500 outline-none">
            <option value="">Severity</option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
          </select>
        )}
      </div>
    </div>
  );
}

function ItemCard({ item, value, onChange }) {
  // value shape: { client, coder, rationale }  (simple items: { coder })
  const v = value || {};
  if (item.type === "diagnosis_row") {
    return <DiagnosisRow item={item} value={value} onChange={onChange} />;
  }
  if (item.simple) {
    return (
      <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/30" data-testid={`oasis-item-${item.id}`}>
        <label className="block text-sm font-medium text-slate-800 mb-2">{item.label}</label>
        <FieldInput item={item} value={v.coder} onChange={(coder) => onChange({ ...v, coder })} />
      </div>
    );
  }
  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/30 sm:col-span-2" data-testid={`oasis-item-${item.id}`}>
      <label className="block text-sm font-medium text-slate-800 mb-3">{item.label}</label>
      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Client response</div>
          <textarea data-testid={`oasis-${item.id}-client`} rows={2} value={v.client || ""}
                    onChange={(e) => onChange({ ...v, client: e.target.value })}
                    placeholder="What the patient/caregiver reported…"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-sky-500 outline-none"/>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Coder response</div>
          <FieldInput item={item} value={v.coder} onChange={(coder) => onChange({ ...v, coder })} />
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Rationale</div>
        <textarea data-testid={`oasis-${item.id}-rationale`} rows={2} value={v.rationale || ""}
                  onChange={(e) => onChange({ ...v, rationale: e.target.value })}
                  placeholder="Why this code/response was chosen (clinical reasoning, source documents, guidance)…"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-sky-500 outline-none"/>
      </div>
    </div>
  );
}

function Section({ section, values, onChange, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`oasis-section-${section.key}`}>
      <button type="button" onClick={() => setOpen(!open)}
              data-testid={`oasis-section-toggle-${section.key}`}
              className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 text-left">
        <span className="font-heading text-base font-medium text-slate-900">{section.title}</span>
        <ChevronDown size={18} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>
      {open && (
        <div className="px-5 pb-5 pt-2 grid sm:grid-cols-2 gap-4 border-t border-slate-100">
          {section.items.map((item) => (
            <ItemCard key={item.id} item={item} value={values[item.id]} onChange={(v) => onChange(item.id, v)} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function FullOasis() {
  const { id } = useParams();
  const [header, setHeader] = useState({ patient_name: "", assessment_date: "", assessment_type: "Start of Care", client_id: "" });
  const [values, setValues] = useState({});
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [loading, setLoading] = useState(Boolean(id));

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api.get(`/oasis/assessments/${id}`)
      .then((r) => {
        setHeader({
          patient_name: r.data.patient_name || "",
          assessment_date: r.data.assessment_date || "",
          assessment_type: r.data.assessment_type || "Start of Care",
          client_id: r.data.client_id || "",
        });
        setValues(r.data.data || {});
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, [id]);

  const setField = (key, v) => setValues((prev) => ({ ...prev, [key]: v }));

  const reset = () => {
    setValues({});
    setHeader({ patient_name: "", assessment_date: "", assessment_type: "Start of Care", client_id: "" });
  };

  const validate = () => {
    if (!header.patient_name || !header.assessment_date) {
      toast.error("Patient name and assessment date are required.");
      return false;
    }
    return true;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await api.post("/oasis/assessments", { ...header, data: values });
      toast.success("Full OASIS saved");
      reset();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  // Save OASIS assessment AND create a chart entry in the QA workflow
  const submitChart = async () => {
    if (!validate()) return;
    const m1021 = (values.M1021?.code || "").trim();
    if (!m1021) {
      toast.error("M1021 — Primary Diagnosis code is required to submit as a chart.");
      return;
    }
    const m1023_codes = Array.from({ length: 25 }, (_, i) => {
      const letter = String.fromCharCode(65 + i);
      const code = (values[`M1023_${letter}`]?.code || "").trim();
      return code ? { slot: letter, code } : null;
    }).filter(Boolean);

    setSubmitting(true);
    try {
      const selectedClient = clients.find((c) => c.id === header.client_id);
      await api.post("/oasis/assessments", { ...header, data: values });
      await api.post("/charts", {
        patient_name: header.patient_name,
        assessment_date: header.assessment_date,
        assessment_type: header.assessment_type,
        m1021_primary: m1021,
        m1023_codes,
        notes: "Submitted from Full OASIS assessment",
        oasis_data: values,
        client_id: header.client_id || null,
        client: selectedClient?.name || "",
      });
      toast.success("Chart submitted to QA queue");
      reset();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto" data-testid="full-oasis-page">
      <PageHeader
        title={id ? "OASIS Assessment" : "Full OASIS Assessment"}
        subtitle={id ? "Viewing a saved assessment. Edit and re-save to create another." : "Complete OASIS-E assessment across all sections. Sections expand/collapse independently."}
        actions={
          <div className="flex flex-wrap gap-2">
            <button onClick={save} disabled={saving || submitting} data-testid="save-full-oasis-btn"
                    className="inline-flex items-center gap-2 bg-white border border-slate-300 text-slate-800 rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-slate-50 disabled:opacity-60">
              <Save size={16}/> {saving ? "Saving…" : "Save assessment"}
            </button>
            <button onClick={() => setPreviewOpen(true)} disabled={saving || submitting} data-testid="preview-full-oasis-btn"
                    className="inline-flex items-center gap-2 bg-white border border-slate-300 text-slate-800 rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-slate-50 disabled:opacity-60">
              <Eye size={16}/> Preview
            </button>
            <button onClick={submitChart} disabled={saving || submitting} data-testid="submit-chart-from-oasis-btn"
                    className="inline-flex items-center gap-2 bg-sky-700 text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-sky-600 disabled:opacity-60">
              <Send size={16}/> {submitting ? "Submitting…" : "Submit chart"}
            </button>
          </div>
        }
      />

      {loading ? (
        <div className="text-slate-500 py-8 text-center">Loading assessment…</div>
      ) : (<>
      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="full-oasis-header">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Patient Name</label>
          <input data-testid="full-oasis-patient-name" required value={header.patient_name}
                 onChange={(e) => setHeader({ ...header, patient_name: e.target.value })}
                 className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 outline-none"/>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Client</label>
          <select data-testid="full-oasis-client" value={header.client_id || ""}
                  onChange={(e) => setHeader({ ...header, client_id: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 outline-none">
            <option value="">— Select client —</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}{c.code ? ` (${c.code})` : ""}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Assessment Date</label>
          <input data-testid="full-oasis-date" type="date" required value={header.assessment_date}
                 onChange={(e) => setHeader({ ...header, assessment_date: e.target.value })}
                 className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 outline-none"/>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Assessment Type</label>
          <select data-testid="full-oasis-type" value={header.assessment_type}
                  onChange={(e) => setHeader({ ...header, assessment_type: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 outline-none">
            {ASSESSMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      <div className="space-y-3">
        {SECTIONS.map((s, i) => (
          <Section key={s.key} section={s} values={values} onChange={setField} defaultOpen={i < 2} />
        ))}
      </div>

      {previewOpen && (
        <PreviewModal
          header={header}
          values={values}
          onClose={() => setPreviewOpen(false)}
          onSubmit={() => { setPreviewOpen(false); submitChart(); }}
          submitting={submitting}
        />
      )}
      </>)}
    </div>
  );
}

function isEmpty(val) {
  if (val == null) return true;
  if (typeof val === "string") return val.trim() === "";
  if (Array.isArray(val)) return val.length === 0;
  if (typeof val === "object") {
    return Object.values(val).every((v) => isEmpty(v));
  }
  return false;
}

function PreviewRow({ item, value }) {
  if (item.type === "diagnosis_row") {
    const v = value || {};
    if (isEmpty(v)) return null;
    return (
      <div className="py-1.5 text-sm grid grid-cols-[180px_1fr] gap-3">
        <div className="text-slate-500">{item.label}</div>
        <div className="text-slate-800 font-mono">
          {v.code || <span className="text-slate-400">—</span>}
          {v.date && <span className="ml-3 text-slate-500 font-sans text-xs">{v.date}</span>}
          {v.oe && <span className="ml-3 text-slate-700 font-sans text-xs">O/E: {v.oe === "O" ? "Onset" : v.oe === "E" ? "Exacerbation" : v.oe}</span>}
          {v.severity && <span className="ml-3 text-slate-700 font-sans text-xs">Severity: {v.severity}</span>}
        </div>
      </div>
    );
  }
  const v = value || {};
  if (isEmpty(v)) return null;
  const display = (x) => Array.isArray(x) ? x.join(", ") : String(x);
  if (item.simple) {
    return (
      <div className="py-1.5 text-sm grid grid-cols-[180px_1fr] gap-3">
        <div className="text-slate-500">{item.label}</div>
        <div className="text-slate-800">{v.coder ? display(v.coder) : <span className="text-slate-400">—</span>}</div>
      </div>
    );
  }
  return (
    <div className="py-2 text-sm border-t border-slate-100 first:border-t-0">
      <div className="font-medium text-slate-800 mb-1">{item.label}</div>
      <div className="grid sm:grid-cols-3 gap-3 text-xs">
        {!isEmpty(v.client) && (
          <div><div className="text-slate-500 mb-0.5">Client</div><div className="text-slate-800">{display(v.client)}</div></div>
        )}
        {!isEmpty(v.coder) && (
          <div><div className="text-slate-500 mb-0.5">Coder</div><div className="text-slate-800">{display(v.coder)}</div></div>
        )}
        {!isEmpty(v.rationale) && (
          <div><div className="text-slate-500 mb-0.5">Rationale</div><div className="text-slate-800">{display(v.rationale)}</div></div>
        )}
      </div>
    </div>
  );
}

function PreviewModal({ header, values, onClose, onSubmit, submitting }) {
  const filledSections = SECTIONS.map((s) => ({
    ...s,
    items: s.items.filter((it) => !isEmpty(values[it.id])),
  })).filter((s) => s.items.length > 0);
  const totalFilled = filledSections.reduce((acc, s) => acc + s.items.length, 0);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="oasis-preview-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <div className="font-heading text-lg font-semibold text-slate-900">Preview — Full OASIS</div>
            <div className="text-xs text-slate-500">{totalFilled} item{totalFilled === 1 ? "" : "s"} filled · Review before submitting</div>
          </div>
          <button onClick={onClose} data-testid="preview-close-btn" className="text-slate-400 hover:text-slate-700 p-1">
            <X size={20}/>
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-4 space-y-5">
          <div className="bg-slate-50 rounded-lg p-3 grid sm:grid-cols-3 gap-3 text-sm">
            <div><div className="text-xs uppercase text-slate-500 tracking-wider mb-0.5">Patient</div><div className="text-slate-900 font-medium">{header.patient_name || "—"}</div></div>
            <div><div className="text-xs uppercase text-slate-500 tracking-wider mb-0.5">Date</div><div className="text-slate-900">{header.assessment_date || "—"}</div></div>
            <div><div className="text-xs uppercase text-slate-500 tracking-wider mb-0.5">Type</div><div className="text-slate-900">{header.assessment_type || "—"}</div></div>
          </div>

          {filledSections.length === 0 && (
            <div className="text-sm text-slate-500 text-center py-8">No items filled yet. Add data to any section to preview.</div>
          )}

          {filledSections.map((s) => (
            <div key={s.key} data-testid={`preview-section-${s.key}`}>
              <div className="font-heading text-sm font-semibold text-sky-800 uppercase tracking-wider mb-2">{s.title}</div>
              <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
                {s.items.map((it) => (
                  <div key={it.id} className="px-3">
                    <PreviewRow item={it} value={values[it.id]} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl">
          <button onClick={onClose} data-testid="preview-edit-btn"
                  className="inline-flex items-center gap-2 bg-white border border-slate-300 text-slate-800 rounded-lg px-4 py-2 text-sm font-medium hover:bg-slate-100">
            Continue editing
          </button>
          <button onClick={onSubmit} disabled={submitting} data-testid="preview-submit-btn"
                  className="inline-flex items-center gap-2 bg-sky-700 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-sky-600 disabled:opacity-60">
            <Send size={16}/> {submitting ? "Submitting…" : "Submit chart"}
          </button>
        </div>
      </div>
    </div>
  );
}
